import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { IntegrityMonitorRealTimePanelWebComponentSchema } from '../types';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_INTEGRITY_MONITOR_POLICY_RULE_SETS_SECTION } from '../../../../../webcomponents.constants';
import { GzTitleWebComponent } from '../../../../../ui/gzTitle';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../../ui/gzRadio';
import { GzLabeledContentWebComponent } from '../../../../../ui/gzLabeledContent';
import { IntegrityMonitorPolicyRuleSetsSectionWebComponent } from '../../integrityMonitorPolicyRuleSetsSection';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { IntegrityMonitorMode } from '@nirvana/shared/dtos/types/policies/common';

export class IntegrityMonitorRealTimePanelView extends BaseWebComponentView {
  private formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;
    $enabledFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $ruleProcessingModeTitle: GzTitleWebComponent;
    $ruleProcessingModeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $ruleProcessingModeOptionHigh: GzRadioWebComponent;
    $ruleProcessingModeOptionNormal: GzRadioWebComponent;
    $ruleProcessingModeOptionLow: GzRadioWebComponent;
    $modeTitle: GzTitleWebComponent;
    $modeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $modeOptionBaseline: GzRadioWebComponent;
    $modeOptionRuleSets: GzRadioWebComponent;
    $ruleSetsSectionLabeledContent: GzLabeledContentWebComponent;
    $ruleSetsSection: IntegrityMonitorPolicyRuleSetsSectionWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $enabledFormField: this.shadow.querySelector('#im-realtime-enabled'),
      $ruleProcessingModeTitle: this.shadow.querySelector('#im-realtime-rule-processing-mode-title'),
      $ruleProcessingModeFormField: this.shadow.querySelector('#im-realtime-rule-processing-mode'),
      $ruleProcessingModeOptionHigh: this.shadow.querySelector('#im-realtime-rule-processing-mode-fast'),
      $ruleProcessingModeOptionNormal: this.shadow.querySelector('#im-realtime-rule-processing-mode-normal'),
      $ruleProcessingModeOptionLow: this.shadow.querySelector('#im-realtime-rule-processing-mode-slow'),
      $modeTitle: this.shadow.querySelector('#im-realtime-mode-title'),
      $modeFormField: this.shadow.querySelector('#im-realtime-mode'),
      $modeOptionBaseline: this.shadow.querySelector('#im-realtime-mode-baseline'),
      $modeOptionRuleSets: this.shadow.querySelector('#im-realtime-mode-rule-sets'),
      $ruleSetsSectionLabeledContent: this.shadow.querySelector('#policy-rule-sets-section-labeled-content'),
      $ruleSetsSection: this.shadow.querySelector(WEB_COMPONENT_INTEGRITY_MONITOR_POLICY_RULE_SETS_SECTION),
    };

    this.getTranslations();
    this.setTranslatedStrings();
    this.injectDependenciesInChildren();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getIntegrityMonitorStrings().policies.realTime;
  }

  setTranslatedStrings() {
    this.dom.$enabledFormField.Input.label = this.dependencies.translateFn('Enable real-time monitoring');
    this.dom.$modeTitle.innerText = this.translations.realTimeMonitoring;
    this.dom.$modeOptionBaseline.label = this.translations.baseline;
    this.dom.$modeOptionRuleSets.label = this.translations.ruleSets;
    this.dom.$ruleSetsSectionLabeledContent.label = this.translations.addRuleSets;

    this.dom.$ruleProcessingModeTitle.innerText = this.translations.ruleProcessingModeTitle;
    this.dom.$ruleProcessingModeOptionHigh.label = this.translations.ruleProcessingModeHigh;
    this.dom.$ruleProcessingModeOptionNormal.label = this.translations.ruleProcessingModeNormal;
    this.dom.$ruleProcessingModeOptionLow.label = this.translations.ruleProcessingModeLow;
  }

  buildSuccessView(data: IntegrityMonitorRealTimePanelWebComponentSchema) {
    this.createFormGroup();
    this.setFormData(data);
    this.setRadioOptionValues();
    this.buildRuleSetsSection(data.formValue.ruleSetIds);
    this.setDisabledChildren(!data.formValue.enabled);
  }

  buildErrorView(error: Error) {
    console.log('Error for loading IM Policy view');
    super.buildErrorView(error);
  }

  public setDisabledChildren(disabled: boolean) {
    this.dom.$modeFormField.disabled = disabled;
    this.dom.$ruleProcessingModeFormField.disabled = disabled;
    this.dom.$ruleSetsSection.disabled = disabled;
  }

  private createFormGroup() {
    this.formGroup = new FormGroup({
      enabled: this.dom.$enabledFormField,
      ruleProcessingMode: this.dom.$ruleProcessingModeFormField,
      mode: this.dom.$modeFormField,
    });
  }

  private setFormData(data) {
    // Initialise the form data with values from Ext
    this.formGroup.value = {
      enabled: data.formValue.enabled,
      mode: data.formValue.mode,
      ruleProcessingMode: data.formValue.ruleProcessingMode,
    };
  }

  private setRadioOptionValues(): void {
    this.dom.$modeOptionBaseline.value = IntegrityMonitorMode.BASELINE;
    this.dom.$modeOptionRuleSets.value = IntegrityMonitorMode.RULE_SETS;
  }

  private injectDependenciesInChildren() {
    if (this.dom.$ruleSetsSection.injectDependencies) {
      this.dom.$ruleSetsSection.injectDependencies(this.dependencies);
      this.dom.$ruleSetsSection.start().catch(console.error);
    }
  }

  private buildRuleSetsSection(ruleSetIds: Array<string>) {
    // After rendering the RuleSetsSection with undefined values, set the real values and re-render
    this.dom.$ruleSetsSection.selectedRuleSetIds = ruleSetIds;
    this.dom.$ruleSetsSection.controller?.render();
  }
}
